import { Injectable } from '@angular/core';
import { Blast } from 'src/app/services/hole.service';
import { Warehouse } from 'src/app/bo/warehouse';
import { Factory } from 'src/app/bo/factory';
import { ProductCost } from 'src/app/bo/products/product-cost';
import { ProductType } from 'src/app/bo/products/productType';
import { Equipment } from 'src/app/bo/equipment';
import { Co2CostResponse } from 'src/app/services/sites.service';

export interface Co2Category {
  name: string;
  translationKey: string;
  value: number;
  percentage: number;
  color: string;
}

export interface Co2BlastSummary {
  total: number;
  categories: Co2Category[];
  transport: TransportCo2Data;
  drilling: DrillingCo2Data[];
  products: ProductCo2Data[];
  detonations: ProductCo2Data[];
}

export interface TransportCo2Data {
  factoryName: string;
  storageName: string;
  factoryToStorage: number;
  storageToSite: number;
  total: number;
}

export interface DrillingCo2Data {
  drillType: string;
  drilledMeters: number;
  co2PerUnit: number;
  total: number;
}

export interface ProductCo2Data {
  productName: string;
  quantity: number;
  co2PerUnit: number;
  total: number;
  isBulk: boolean;
}

export interface Co2CalculationContext {
  blast: Blast;
  productCosts: ProductCost[];
  co2Costs: Co2CostResponse[];
  warehouses: Warehouse[];
  factories: Factory[];
  equipments: Equipment[];
  siteCoordY?: number;
  siteCoordX?: number;
}

@Injectable({
  providedIn: 'root'
})
export class Co2CalculatorService {

  private static readonly DEFAULT_GES_TRANSPORT_FACTOR = 0.0001;

  private static readonly CATEGORY_COLORS = {
    product:    'var(--c-co2-product)',
    transport:  'var(--c-co2-transport)',
    drilling:   'var(--c-co2-drilling)',
    detonation: 'var(--c-co2-detonation)'
  };

  calculateSummary(ctx: Co2CalculationContext): Co2BlastSummary {
    const transportData = this.getTransportData(ctx);

    const categories: Co2Category[] = [
      {
        name: 'Production',
        translationKey: 'ENVIRONMENT.PRODUCTION',
        value: this.calculateProductCo2(ctx),
        percentage: 0,
        color: Co2CalculatorService.CATEGORY_COLORS.product
      },
      {
        name: 'Transport',
        translationKey: 'ENVIRONMENT.TRANSPORT',
        value: transportData.total,
        percentage: 0,
        color: Co2CalculatorService.CATEGORY_COLORS.transport
      },
      {
        name: 'Drilling',
        translationKey: 'ENVIRONMENT.DRILLING',
        value: this.calculateDrillingCo2(ctx),
        percentage: 0,
        color: Co2CalculatorService.CATEGORY_COLORS.drilling
      },
      {
        name: 'Detonation',
        translationKey: 'ENVIRONMENT.DETONATION',
        value: this.calculateDetonationCo2(ctx),
        percentage: 0,
        color: Co2CalculatorService.CATEGORY_COLORS.detonation
      }
    ];

    const total = categories.reduce((sum, cat) => sum + cat.value, 0);
    categories.forEach(cat => {
      cat.percentage = total > 0 ? (cat.value / total) * 100 : 0;
    });

    return {
      total,
      categories,
      transport: transportData,
      drilling: this.getDrillingData(ctx),
      products: this.getProductData(ctx),
      detonations: this.getDetonationData(ctx)
    };
  }

  // --- Transport ---

  private getTransportData(ctx: Co2CalculationContext): TransportCo2Data {
    if (!ctx.blast?.Holes || !ctx.co2Costs.length || !ctx.warehouses.length) {
      return { factoryName: '-', storageName: '-', factoryToStorage: 0, storageToSite: 0, total: 0 };
    }

    let totalFactoryToStorage = 0;
    let totalStorageToSite = 0;
    let factoryName = '-';
    let storageName = '-';

    const productQuantities = this.getProductQuantities(ctx.blast);

    const usedWarehouseIds = new Set<number>();
    productQuantities.forEach((_, productId) => {
      const co2Cost = ctx.co2Costs.find(c => c.productId === productId);
      if (co2Cost?.warehouseId) {
        usedWarehouseIds.add(co2Cost.warehouseId);
      }
    });

    if (usedWarehouseIds.size > 0) {
      const firstWarehouseId = usedWarehouseIds.values().next().value;
      const warehouse = ctx.warehouses.find(w => w.warehouseId === firstWarehouseId);
      if (warehouse) {
        storageName = warehouse.name || '-';
        factoryName = warehouse.factoryName || ctx.factories.find(f => f.factoryId === warehouse.factoryId)?.name || '-';
      }
    }

    // Calculate transport CO2 once per unique warehouse route
    usedWarehouseIds.forEach(warehouseId => {
      const warehouse = ctx.warehouses.find(w => w.warehouseId === warehouseId);
      if (!warehouse || !warehouse.latitude || !warehouse.longitude || !warehouse.factoryId) return;

      const factory = ctx.factories.find(f => f.factoryId === warehouse.factoryId);
      if (!factory || !factory.latitude || !factory.longitude) return;

      const factor = warehouse.co2perKm ?? Co2CalculatorService.DEFAULT_GES_TRANSPORT_FACTOR;

      const distFactoryWarehouse = this.calculateDistance(
        factory.latitude, factory.longitude,
        warehouse.latitude, warehouse.longitude
      );
      totalFactoryToStorage += distFactoryWarehouse * factor;

      if (ctx.siteCoordY && ctx.siteCoordX) {
        const distWarehouseSite = this.calculateDistance(
          warehouse.latitude, warehouse.longitude,
          ctx.siteCoordY, ctx.siteCoordX
        );
        totalStorageToSite += distWarehouseSite * factor;
      }
    });

    return {
      factoryName,
      storageName,
      factoryToStorage: Math.round(totalFactoryToStorage * 100) / 100,
      storageToSite: Math.round(totalStorageToSite * 100) / 100,
      total: Math.round((totalFactoryToStorage + totalStorageToSite) * 100) / 100
    };
  }

  // --- Production ---

  private calculateProductCo2(ctx: Co2CalculationContext): number {
    if (!ctx.blast?.Holes) return 0;

    let totalCo2 = 0;
    ctx.blast.Holes.forEach(hole => {
      const charges = hole.Loading?.actualCharge?.chargeElements ||
                      hole.Loading?.plannedCharge?.chargeElements || [];
      charges.forEach(charge => {
        const productCost = ctx.productCosts.find(p => p.productId === charge.productId);
        if (productCost) {
          const gesProduction = productCost.gesProduction || 0;
          const weightKg = this.getProductWeightKg(productCost);
          totalCo2 += gesProduction * weightKg * (charge.quantity || 0);
        }
      });
    });
    return totalCo2;
  }

  private getProductData(ctx: Co2CalculationContext): ProductCo2Data[] {
    if (!ctx.blast?.Holes) return [];

    const productMap = new Map<number, ProductCo2Data>();

    ctx.blast.Holes.forEach(hole => {
      const charges = hole.Loading?.actualCharge?.chargeElements ||
                      hole.Loading?.plannedCharge?.chargeElements || [];
      charges.forEach(charge => {
        if (charge.productId === 0) return;
        const existing = productMap.get(charge.productId);
        const productCost = ctx.productCosts.find(p => p.productId === charge.productId);
        const gesProduction = productCost?.gesProduction || 0;
        const weightKg = productCost ? this.getProductWeightKg(productCost) : 1;
        const co2PerUnit = gesProduction * weightKg;
        const isBulk = productCost?.productType === ProductType.Bulk;

        if (existing) {
          existing.quantity += charge.quantity || 0;
          existing.total = existing.quantity * existing.co2PerUnit;
        } else {
          productMap.set(charge.productId, {
            productName: productCost?.fullname || `Product ${charge.productId}`,
            quantity: charge.quantity || 0,
            co2PerUnit,
            total: (charge.quantity || 0) * co2PerUnit,
            isBulk
          });
        }
      });
    });

    return Array.from(productMap.values());
  }

  // --- Detonation ---

  private calculateDetonationCo2(ctx: Co2CalculationContext): number {
    if (!ctx.blast?.Holes) return 0;

    let totalCo2 = 0;
    ctx.blast.Holes.forEach(hole => {
      const charges = hole.Loading?.actualCharge?.chargeElements ||
                      hole.Loading?.plannedCharge?.chargeElements || [];
      charges.forEach(charge => {
        const productCost = ctx.productCosts.find(p => p.productId === charge.productId);
        if (productCost) {
          const gesDetonation = productCost.gesDetonation || 0;
          const weightKg = this.getProductWeightKg(productCost);
          totalCo2 += gesDetonation * weightKg * (charge.quantity || 0);
        }
      });
    });
    return totalCo2;
  }

  private getDetonationData(ctx: Co2CalculationContext): ProductCo2Data[] {
    if (!ctx.blast?.Holes) return [];

    const productMap = new Map<number, ProductCo2Data>();

    ctx.blast.Holes.forEach(hole => {
      const charges = hole.Loading?.actualCharge?.chargeElements ||
                      hole.Loading?.plannedCharge?.chargeElements || [];
      charges.forEach(charge => {
        if (charge.productId === 0) return;
        const existing = productMap.get(charge.productId);
        const productCost = ctx.productCosts.find(p => p.productId === charge.productId);
        const gesDetonation = productCost?.gesDetonation || 0;
        const weightKg = productCost ? this.getProductWeightKg(productCost) : 1;
        const co2PerUnit = gesDetonation * weightKg;
        const isBulk = productCost?.productType === ProductType.Bulk;

        if (existing) {
          existing.quantity += charge.quantity || 0;
          existing.total = existing.quantity * existing.co2PerUnit;
        } else {
          productMap.set(charge.productId, {
            productName: productCost?.fullname || `Product ${charge.productId}`,
            quantity: charge.quantity || 0,
            co2PerUnit,
            total: (charge.quantity || 0) * co2PerUnit,
            isBulk
          });
        }
      });
    });

    return Array.from(productMap.values());
  }

  // --- Drilling ---

  private calculateDrillingCo2(ctx: Co2CalculationContext): number {
    const drill = this.getDrillEquipment(ctx.equipments);
    if (!drill) return 0;

    const totalDrilledMeters = this.getTotalDrilledMeters(ctx.blast);
    return (drill.co2EqPerDrilledMeter || 0) * totalDrilledMeters;
  }

  private getDrillingData(ctx: Co2CalculationContext): DrillingCo2Data[] {
    const drill = this.getDrillEquipment(ctx.equipments);
    if (!drill) return [];

    const totalDrilledMeters = this.getTotalDrilledMeters(ctx.blast);

    return [{
      drillType: drill.name || '-',
      drilledMeters: totalDrilledMeters,
      co2PerUnit: drill.co2EqPerDrilledMeter || 0,
      total: (drill.co2EqPerDrilledMeter || 0) * totalDrilledMeters
    }];
  }

  // --- Helpers ---

  private getDrillEquipment(equipments: Equipment[]): Equipment | undefined {
    return equipments.find(e => e.co2EqPerDrilledMeter && e.co2EqPerDrilledMeter > 0);
  }

  private getTotalDrilledMeters(blast: Blast): number {
    if (!blast?.Holes) return 0;
    let total = 0;
    blast.Holes.forEach(hole => {
      const drilledLength = hole.Geometry?.drilllength ?? hole.Geometry?.length ?? 0;
      total += drilledLength;
    });
    return total;
  }

  private getProductQuantities(blast: Blast): Map<number, number> {
    const productQuantities = new Map<number, number>();
    blast.Holes.forEach(hole => {
      const charges = hole.Loading?.actualCharge?.chargeElements ||
                      hole.Loading?.plannedCharge?.chargeElements || [];
      charges.forEach(charge => {
        const existing = productQuantities.get(charge.productId) || 0;
        productQuantities.set(charge.productId, existing + (charge.quantity || 0));
      });
    });
    return productQuantities;
  }

  private extractWeightFromFullname(fullname: string): number | null {
    const match = fullname?.match(/\/\s*(\d+)\s*g\s*\)/i);
    return match ? parseInt(match[1], 10) : null;
  }

  private getProductWeightKg(product: ProductCost): number {
    const weightGrams = this.extractWeightFromFullname(product.fullname);
    return weightGrams ? weightGrams / 1000 : 1;
  }

  private calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    if (!lat1 || !lon1 || !lat2 || !lon2) return 0;

    const R = 6371;
    const dLat = this.toRad(lat2 - lat1);
    const dLon = this.toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRad(lat1)) * Math.cos(this.toRad(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  private toRad(deg: number): number {
    return deg * (Math.PI / 180);
  }
}
